/**
 * CustomTextManager handles local storage operations for user-uploaded custom texts
 * Only premium users can use this feature
 */
export class CustomTextManager {
  private static instance: CustomTextManager;
  private customTexts: Map<string, string[]>; // userId -> array of texts
  private readonly STORAGE_KEY = 'typeRush_customTexts';
  
  private constructor() {
    this.customTexts = new Map();
    this.loadFromStorage();
  }
  
  public static getInstance(): CustomTextManager {
    if (!CustomTextManager.instance) {
      CustomTextManager.instance = new CustomTextManager();
    }
    return CustomTextManager.instance;
  }
  
  /**
   * Add a custom text for a user
   * @param userId The user ID
   * @param text The custom text to add
   * @returns true if successful, false otherwise
   */
  public addCustomText(userId: string, text: string): boolean {
    try {
      // Get existing texts or create new array
      const userTexts = this.customTexts.get(userId) || [];
      
      // Add new text
      userTexts.push(text);
      
      // Store back
      this.customTexts.set(userId, userTexts);
      
      // Save to localStorage
      this.saveToStorage();
      return true;
    } catch (error) {
      console.error('Failed to add custom text:', error);
      return false;
    }
  }
  
  /**
   * Add a custom text from a file
   * @param userId The user ID
   * @param file The text file to read
   * @returns Promise resolving to true if successful, false otherwise
   */
  public async addCustomTextFromFile(userId: string, file: File): Promise<boolean> {
    try {
      // Check file size (max 100KB)
      if (file.size > 100 * 1024) {
        throw new Error('File too large. Maximum size: 100KB');
      }
      
      // Read file content
      const text = await this.readFileContent(file);
      
      // Validate text length (max 10,000 chars)
      if (text.length > 10000) {
        throw new Error('Text too long. Maximum length: 10,000 characters');
      }
      
      // Add text
      return this.addCustomText(userId, text);
    } catch (error) {
      console.error('Failed to add custom text from file:', error);
      return false;
    }
  }
  
  /**
   * Read text file content
   * @param file The file to read
   * @returns Promise resolving to the file content
   */
  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      file.text().then(text => {
        resolve(text);
      }).catch(() => {
        reject(new Error('Error reading file'));
      });
    });
  }
  
  /**
   * Get all custom texts for a user
   * @param userId The user ID
   * @returns Array of custom texts or empty array if none found
   */
  public getCustomTexts(userId: string): string[] {
    return this.customTexts.get(userId) || [];
  }
  
  /**
   * Remove a custom text for a user
   * @param userId The user ID
   * @param index The index of the text to remove
   * @returns true if successful, false otherwise
   */
  public removeCustomText(userId: string, index: number): boolean {
    try {
      // Get existing texts
      const userTexts = this.customTexts.get(userId);
      
      // Check if texts exist
      if (!userTexts || index >= userTexts.length) {
        return false;
      }
      
      // Remove text at index
      userTexts.splice(index, 1);
      
      // Store back
      this.customTexts.set(userId, userTexts);
      
      // Save to localStorage
      this.saveToStorage();
      return true;
    } catch (error) {
      console.error('Failed to remove custom text:', error);
      return false;
    }
  }
  
  /**
   * Save data to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(Array.from(this.customTexts.entries())));
    } catch (error) {
      console.warn('Failed to save custom texts to localStorage:', error);
    }
  }
  
  /**
   * Load data from localStorage
   */
  private loadFromStorage(): void {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      
      if (storedData) {
        this.customTexts = new Map(JSON.parse(storedData));
      }
    } catch (error) {
      console.warn('Failed to load custom texts from localStorage:', error);
    }
  }
  
  /**
   * Clear all custom texts (for testing/development)
   */
  public clearAll(): void {
    this.customTexts.clear();
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

// Export a singleton instance
export const customTextManager = CustomTextManager.getInstance();
